/**
 * Leitner scheduling: cards live in boxes 1..5. A correct answer moves a card up
 * one box, a wrong answer sends it straight back to box 1. Low boxes are picked
 * far more often, so difficult words come up again quickly.
 */
import type { AnswerGrade, AskDirection, Card, Direction, Question, VocabSet } from './types';
import { BOX_COUNT } from './types';
import { gradeAnswer } from './answer';

/** Selection weight per box: box 1 is picked 16x as often as box 5. */
const BOX_WEIGHTS = [16, 8, 4, 2, 1];

export function boxWeight(box: number): number {
  const index = Math.min(Math.max(box, 1), BOX_COUNT) - 1;
  return BOX_WEIGHTS[index];
}

/** Applies the result of one answer to the card (mutating a copy is the caller's job). */
export function applyGrade(card: Card, grade: AnswerGrade, now = Date.now()): Card {
  const updated: Card = { ...card, asked: card.asked + 1, lastAsked: now };

  if (grade === 'correct') {
    updated.correct = card.correct + 1;
    updated.box = Math.min(card.box + 1, BOX_COUNT);
  } else if (grade === 'near') {
    // "Almost right" is neither rewarded nor punished: the box stays where it is.
    updated.correct = card.correct;
  } else {
    updated.box = 1;
  }

  return updated;
}

/** Progress of a set as a value between 0 and 1, based on the Leitner boxes. */
export function setProgress(set: VocabSet): number {
  return cardsProgress(set.cards);
}

/** Average box position of a list of cards, 0 (all new) to 1 (all mastered). */
export function cardsProgress(cards: Card[]): number {
  if (cards.length === 0) return 0;
  const earned = cards.reduce((sum, card) => sum + (card.box - 1), 0);
  return earned / (cards.length * (BOX_COUNT - 1));
}

export function boxHistogram(set: VocabSet): number[] {
  const histogram = new Array<number>(BOX_COUNT).fill(0);
  for (const card of set.cards) {
    const index = Math.min(Math.max(card.box, 1), BOX_COUNT) - 1;
    histogram[index] += 1;
  }
  return histogram;
}

/**
 * Picks the next card using weighted random selection.
 * `excludeId` prevents asking the same card twice in a row.
 */
export function pickCard(
  cards: Card[],
  excludeId: string | null,
  random: () => number = Math.random,
): Card | null {
  if (cards.length === 0) return null;

  let pool = cards.filter((card) => card.id !== excludeId);
  if (pool.length === 0) pool = cards;

  const total = pool.reduce((sum, card) => sum + boxWeight(card.box), 0);
  let ticket = random() * total;

  for (const card of pool) {
    ticket -= boxWeight(card.box);
    if (ticket <= 0) return card;
  }

  return pool[pool.length - 1];
}

export function resolveDirection(
  direction: Direction,
  random: () => number = Math.random,
): AskDirection {
  if (direction === 'mixed') {
    return random() < 0.5 ? 'native2foreign' : 'foreign2native';
  }
  return direction;
}

function shuffle<T>(items: T[], random: () => number): T[] {
  const result = items.slice();
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/** Builds a question, including multiple choice distractors from the same set. */
export function buildQuestion(
  card: Card,
  direction: AskDirection,
  allCards: Card[],
  withOptions: boolean,
  random: () => number = Math.random,
): Question {
  const prompt = direction === 'native2foreign' ? card.native : card.foreign;
  const answer = direction === 'native2foreign' ? card.foreign : card.native;

  if (!withOptions) {
    return { card, direction, prompt, answer };
  }

  const pool = allCards
    .filter((other) => other.id !== card.id)
    .map((other) => (direction === 'native2foreign' ? other.foreign : other.native))
    // A distractor that would be graded as correct (a variant of the answer, or
    // only one typo away) must not appear as a wrong option.
    .filter((word) => gradeAnswer(word, answer) === 'wrong');

  const distractors = shuffle(Array.from(new Set(pool)), random).slice(0, 3);
  const options = shuffle([answer, ...distractors], random);

  return { card, direction, prompt, answer, options };
}
